<script lang="ts">
export default {
  name: 'KkSplitButton',
}
</script>

<script lang="ts" setup>
import { onBeforeUnmount, watch, ref } from 'vue'

export type SplitButtonOption = {
  key: number
  name: string
}

export interface Props {
  options: SplitButtonOption[]
  currentOption?: SplitButtonOption
  placeholder?: string
}

export interface Emits {
  (key: 'click', event: MouseEvent): void
  (key: 'select-option', value: SplitButtonOption): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()
const showOptions = ref(false)

const leftBtnRef = ref<HTMLDivElement | null>(null)

onBeforeUnmount(() => {
  window.removeEventListener('click', closeOption)
})

const onClickSelectButton = () => {
  showOptions.value = !showOptions.value
}

const selectOption = (option: SplitButtonOption) => {
  showOptions.value = false
  emit('select-option', option)
}

const onClickSubmitButton = (event: MouseEvent) => {
  showOptions.value = false
  emit('click', event)
}

const closeOption = (event: MouseEvent) => {
  if (leftBtnRef.value && event.target && !leftBtnRef.value.contains(event.target as Node)) {
    showOptions.value = false
  }
}

watch(showOptions, (newValue) => {
  if (newValue) {
    window.addEventListener('click', closeOption)
  } else {
    window.removeEventListener('click', closeOption)
  }
})
</script>

<template>
  <div ref="leftBtnRef" class="selectableButton">
    <button
      type="button"
      class="selectableButton__button"
      @click="onClickSubmitButton"
    >
      {{ props.currentOption?.name || placeholder }}
    </button>
    <span class="selectableButton__selectButton" @click="onClickSelectButton()" />
    <div v-show="showOptions" class="selectableButton__selectOptionsContainer">
      <ul ref="leftBtnRef" class="selectableButton__selectOptions">
        <li v-for="(option) in props.options"
            :key="option.key"
            @click="selectOption(option)"
        >
          <slot name="option" :option="option" />
        </li>
      </ul>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import "../../../../../stylesheets/biz_spa/foundation/_var.scss";
.selectableButton {
  display: inline-block;
  position: relative;
  height: 44px;
  font-size: 14px;
  &__button {
    min-width: 100px;
    height: inherit;
    padding: 0 14px;
    border: 1px solid $color-accent-primary;
    border-radius: 4px 0 0 4px;
    background-color: $color-accent-primary;
    color: #FFF;
    font-weight: bold;
    vertical-align: middle;
    cursor: pointer;
    &:hover {
      background-color: lighten($color-accent-primary, 5%);
    }
  }
  &__selectButton {
    display: inline-block;
    width: 32px;
    height: inherit;
    margin-left: 1px;
    border: 1px solid $color-accent-primary;
    border-radius: 0 4px 4px 0;
    background-color: $color-accent-primary;
    vertical-align: middle;
    cursor: pointer;
    &::before {
      position: absolute;
      top: 50%;
      right: 12px;
      z-index: 1;
      transform: translateY(-50%) rotate(180deg);
      border-width: 0 4px 4px;
      border-style: solid;
      border-color: transparent transparent #FFF;
      content: "";
      pointer-events: none;
    }
    &:hover {
      background-color: lighten($color-accent-primary, 5%);
    }
  }
  &__selectOptionsContainer {
    position: absolute;
    min-width: 100%;
    max-height: 240px;
    padding: 8px 0;
    border-radius: 4px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, .05);
    overflow-x: auto;
    background: #FFF;
    white-space: nowrap;
  }
  &__selectOptions {
    li {
      display: block;
      width: 100%;
      padding: 12px 24px;
      text-align: left;
      cursor: pointer;
      transition: .2s;
      &:hover,
      &.is-current {
        background-color: #E5E5E5;
      }
    }
  }
}
</style>
